import { useRef, useState, useCallback, useEffect } from 'react';
import { motion, springs, Pressable } from '../lib/motion';
import { haptic } from '../lib/haptics';

const SWIPE_THRESHOLD = 60;

type SwipeDirection = 'left' | 'right' | null;

interface SwipeCardProps {
  children: React.ReactNode;
  enabled: boolean;
  onSwipeRight: () => void;
  onSwipeLeft: () => void;
}

export function SwipeCard({ children, enabled, onSwipeRight, onSwipeLeft }: SwipeCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const currentX = useRef(0);
  const tracking = useRef(false);
  const axis = useRef<'h' | 'v' | null>(null);
  const fired = useRef(false);
  const [offset, setOffset] = useState(0);
  const [exiting, setExiting] = useState<SwipeDirection>(null);

  // Store callbacks in refs so native listeners always see current values
  const onSwipeRightRef = useRef(onSwipeRight);
  const onSwipeLeftRef = useRef(onSwipeLeft);
  const enabledRef = useRef(enabled);
  onSwipeRightRef.current = onSwipeRight;
  onSwipeLeftRef.current = onSwipeLeft;
  enabledRef.current = enabled;

  // Reset state when enabled changes (new question answered)
  useEffect(() => {
    setOffset(0);
    setExiting(null);
    tracking.current = false;
    axis.current = null;
    fired.current = false;
  }, [enabled]);

  // Attach native touch listeners with { passive: false } so preventDefault works on iOS
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (!enabledRef.current || fired.current) return;
      const touch = e.touches[0]!;
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      currentX.current = 0;
      tracking.current = true;
      axis.current = null;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!tracking.current || !enabledRef.current) return;
      const touch = e.touches[0]!;
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;

      // Lock axis on first movement
      if (axis.current === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          axis.current = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
        }
        return;
      }

      // Vertical — stop tracking, let browser scroll
      if (axis.current === 'v') {
        tracking.current = false;
        return;
      }

      // Horizontal — prevent scroll, track offset
      e.preventDefault();
      e.stopPropagation();
      currentX.current = dx;
      setOffset(dx);
    };

    const handleTouchEnd = () => {
      if (!tracking.current || !enabledRef.current || axis.current !== 'h') {
        tracking.current = false;
        axis.current = null;
        return;
      }
      tracking.current = false;
      axis.current = null;

      const dx = currentX.current;
      if (dx > SWIPE_THRESHOLD) {
        fired.current = true;
        haptic.success();
        setExiting('right');
        setTimeout(() => onSwipeRightRef.current(), 200);
      } else if (dx < -SWIPE_THRESHOLD) {
        fired.current = true;
        haptic.warning();
        setExiting('left');
        setTimeout(() => onSwipeLeftRef.current(), 200);
      } else {
        setOffset(0);
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const direction: SwipeDirection = offset > 30 ? 'right' : offset < -30 ? 'left' : null;
  const rotation = offset * 0.04;
  const exitX = exiting === 'right' ? 480 : exiting === 'left' ? -480 : 0;
  const dragProgress = Math.min(1, Math.abs(offset) / SWIPE_THRESHOLD);

  return (
    <div className="relative">
      {/* Background indicators */}
      {enabled && (direction || exiting) && (
        <>
          {(direction === 'right' || exiting === 'right') && (
            <div
              className="absolute inset-0 rounded-3xl border-2 border-emerald-300 bg-emerald-50 flex items-center justify-center overflow-hidden"
              style={{ opacity: exiting ? 1 : dragProgress }}
            >
              <div
                className="flex flex-col items-center gap-2"
                style={{ transform: `scale(${exiting ? 1 : 0.75 + 0.25 * dragProgress})` }}
              >
                <div className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-pop">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                </div>
                <span className="font-display font-bold text-lg text-emerald-600">Got it</span>
              </div>
            </div>
          )}
          {(direction === 'left' || exiting === 'left') && (
            <div
              className="absolute inset-0 rounded-3xl border-2 border-amber-300 bg-amber-50 flex items-center justify-center overflow-hidden"
              style={{ opacity: exiting ? 1 : dragProgress }}
            >
              <div
                className="flex flex-col items-center gap-2"
                style={{ transform: `scale(${exiting ? 1 : 0.75 + 0.25 * dragProgress})` }}
              >
                <div className="w-14 h-14 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-pop">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/></svg>
                </div>
                <span className="font-display font-bold text-lg text-amber-600">Review</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Swipeable card */}
      <div
        ref={cardRef}
        style={{
          transform: exiting
            ? `translateX(${exitX}px) rotate(${exitX * 0.045}deg)`
            : offset !== 0
              ? `translateX(${offset}px) rotate(${rotation}deg)`
              : undefined,
          opacity: exiting ? 0 : 1,
          transition: tracking.current ? 'none' : 'transform 0.2s cubic-bezier(0.32, 0.72, 0.35, 1), opacity 0.2s ease-out',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {children}
      </div>

      {/* Tap buttons as fallback */}
      {enabled && !exiting && (
        <div className="flex justify-between gap-3 mt-4 px-1">
          <button
            onClick={() => {
              haptic.warning();
              fired.current = true;
              setExiting('left');
              setTimeout(onSwipeLeft, 200);
            }}
            className="flex items-center justify-center gap-1.5 text-sm text-amber-600 font-semibold px-4 py-2.5 min-h-[44px] rounded-full border-2 border-amber-200 bg-amber-50 shadow-card active:scale-95 transition-transform"
          >
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" /></svg>
            Mark for Review
          </button>
          <button
            onClick={() => {
              haptic.success();
              fired.current = true;
              setExiting('right');
              setTimeout(onSwipeRight, 200);
            }}
            className="flex items-center justify-center gap-1.5 text-sm text-emerald-600 font-semibold px-4 py-2.5 min-h-[44px] rounded-full border-2 border-emerald-200 bg-emerald-50 shadow-card active:scale-95 transition-transform"
          >
            Got it
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ── Swipe Tutorial Overlay ──────────────────────────────────────────────────

const TUTORIAL_DISMISSED_KEY = 'swipe-tutorial-dismissed';

export function useSwipeTutorial() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(TUTORIAL_DISMISSED_KEY);
    if (!dismissed) setShow(true);
  }, []);

  const dismiss = useCallback(() => {
    setShow(false);
    localStorage.setItem(TUTORIAL_DISMISSED_KEY, '1');
  }, []);

  return { showTutorial: show, dismissTutorial: dismiss };
}

export function SwipeTutorialOverlay({ onDismiss }: { onDismiss: () => void }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: 'Swipe Right',
      subtitle: 'Got it',
      desc: 'When you understand the question and explanation, swipe right to move on.',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50 border-emerald-200',
      arrow: 'right' as const,
    },
    {
      title: 'Swipe Left',
      subtitle: 'Mark for Review',
      desc: 'Need to revisit this? Swipe left to flag it and come back later.',
      color: 'text-amber-600',
      bg: 'bg-amber-50 border-amber-200',
      arrow: 'left' as const,
    },
  ];

  const current = steps[step]!;
  const dir = current.arrow === 'right' ? 1 : -1;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={onDismiss}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      <motion.div
        className="relative bg-white rounded-3xl max-w-sm w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={springs.bouncy}
      >
        <div className={`px-6 pt-8 pb-5 ${current.bg} border-b`}>
          <div className="relative w-24 h-20 mx-auto mb-4" key={step}>
            <motion.div
              className="w-16 h-20 bg-white rounded-2xl border-2 border-slate-200 shadow-card mx-auto"
              animate={{ x: [0, dir * 10, 0], rotate: [0, dir * 5, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className={`absolute top-1/2 ${current.arrow === 'right' ? 'right-0' : 'left-0'}`}
              style={{ y: '-50%' }}
              animate={{ x: [0, dir * 10, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <svg className={`w-8 h-8 ${current.arrow === 'right' ? 'text-emerald-500' : 'text-amber-500'}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {current.arrow === 'right'
                  ? <path d="M5 12h14M12 5l7 7-7 7" />
                  : <path d="M19 12H5M12 19l-7-7 7-7" />}
              </svg>
            </motion.div>
          </div>
          <h3 className={`font-display text-xl font-bold text-center ${current.color}`}>{current.title}</h3>
          <p className="text-sm text-center text-slate-500 mt-1">{current.subtitle}</p>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600 text-center leading-relaxed">{current.desc}</p>

          <div className="flex justify-center gap-2 mt-5">
            {steps.map((_, i) => (
              <div key={i} className={`h-2 rounded-full transition-all duration-300 ${i === step ? 'w-5 bg-squishy-teal' : 'w-2 bg-slate-200'}`} />
            ))}
          </div>

          <div className="mt-5 flex gap-3">
            {step < steps.length - 1 ? (
              <>
                <Pressable onClick={onDismiss} className="flex-1 text-sm font-medium text-slate-400 py-2.5 rounded-xl border border-slate-200">
                  Skip
                </Pressable>
                <Pressable onClick={() => setStep(step + 1)}
                  className="flex-1 bg-gradient-to-r from-squishy-teal to-squishy-tealDark text-white py-2.5 rounded-xl text-sm font-bold shadow-card">
                  Next
                </Pressable>
              </>
            ) : (
              <Pressable onClick={onDismiss}
                className="w-full bg-gradient-to-r from-squishy-teal to-squishy-tealDark text-white py-2.5 rounded-xl text-sm font-bold shadow-card">
                Let's Go!
              </Pressable>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
